import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { Device, Call } from "@twilio/voice-sdk";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";
import { useCallFinalization } from "@/hooks/useCallFinalization";

interface TwilioContextType {
  device: Device | null;
  isReady: boolean;
  isConnecting: boolean;
  isOnCall: boolean;
  isInitiatingCall: boolean;
  currentCall: Call | null;
  incomingCall: Call | null;
  error: string | null;
  callError: { code: number; message: string } | null;
  outgoingNumber: string | null;
  currentCallTargetNumber: string | null;
  currentCallRecordId: string | null;
  currentConsultantId: string | null;
  isOnHold: boolean;
  isParked: boolean;
  parkedQueue: string | null;
  parkedClientName: string | null;
  parkedClientPhone: string | null;
  makeCall: (phoneNumber: string, contactId?: string, leadId?: string) => Promise<Call>;
  makeConsultantCall: (phoneNumber: string, consultantUserId: string) => Promise<Call>;
  makeRingAllCall: () => Promise<Call>;
  joinDialerConference: (conferenceName: string) => Promise<Call | null>;
  isDialerCall: boolean;
  isDialerAudioReady: boolean;

  hangUp: () => void;
  toggleMute: () => boolean;
  acceptIncomingCall: (call: Call) => Promise<void>;
  rejectIncomingCall: (call: Call) => void;
  transferCall: (destination: string, destinationUserId?: string) => Promise<void>;
  transferParkedToConsultant: (consultantUserId: string) => Promise<void>;
  holdCall: () => Promise<void>;
  resumeCall: () => Promise<void>;
  sendDtmf: (digit: string) => void;
  instantDisconnect: boolean;
  clearInstantDisconnect: () => void;
}

const TwilioContext = createContext<TwilioContextType | undefined>(undefined);

export function TwilioProvider({ children }: { children: React.ReactNode }) {
  const [device, setDevice] = useState<Device | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isOnCall, setIsOnCall] = useState(false);
  const [isInitiatingCall, setIsInitiatingCall] = useState(false);
  const [currentCall, setCurrentCall] = useState<Call | null>(null);
  const [incomingCall, setIncomingCall] = useState<Call | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [callError, setCallError] = useState<{ code: number; message: string } | null>(null);
  const [outgoingNumber, setOutgoingNumber] = useState<string | null>(null);
  const [currentCallTargetNumber, setCurrentCallTargetNumber] = useState<string | null>(null);
  const [currentCallRecordId, setCurrentCallRecordId] = useState<string | null>(null);
  const [currentAppUserId, setCurrentAppUserId] = useState<string | null>(null);
  const [currentConsultantId, setCurrentConsultantId] = useState<string | null>(null);
  const [isOnHold, setIsOnHold] = useState(false);
  const [isParked, setIsParked] = useState(false);
  const [parkedQueue, setParkedQueue] = useState<string | null>(null);
  const [parkedClientName, setParkedClientName] = useState<string | null>(null);
  const [parkedClientPhone, setParkedClientPhone] = useState<string | null>(null);
  const [parkedCallInfo, setParkedCallInfo] = useState<{
    contactName?: string;
    contactPhone: string;
    direction: "inbound" | "outbound";
    callRecordId?: string;
    customerCallSid?: string;
  } | null>(null);
  const [isPickingUp, setIsPickingUp] = useState(false);
  const [instantDisconnect, setInstantDisconnect] = useState(false);
  // Marca que a chamada atual pertence ao Power Dialer (console próprio cuida da UI)
  const [isDialerCall, setIsDialerCall] = useState(false);
  const [isDialerAudioReady, setIsDialerAudioReady] = useState(false);

  const isParkedRef = useRef(false);
  useEffect(() => {
    isParkedRef.current = isParked;
  }, [isParked]);

  const isPickingUpRef = useRef(false);
  useEffect(() => {
    isPickingUpRef.current = isPickingUp;
  }, [isPickingUp]);

  // Track if device has been registered at least once
  const hasRegisteredOnce = useRef(false);

  // Registration retry refs
  const registrationTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const retryCountRef = useRef(0);
  const MAX_REGISTRATION_RETRIES = 3;

  // Debounce entre tentativas de re-registro (evita corrida error/unregistered + retry do SDK)
  const lastReRegisterAtRef = useRef<number>(0);
  const REREGISTER_DEBOUNCE_MS = 5000;
  // Ref com a chamada ativa para decidir se deixamos o SDK reconectar sozinho
  const currentCallRef = useRef<Call | null>(null);


  // Inicializa o dispositivo Twilio SOMENTE quando autenticado
  const { user, session, loading } = useAuth();
  const { ensureCallFinalized, cleanupStaleCalls } = useCallFinalization();
  const currentCallRecordIdRef = useRef<string | null>(null);
  const callConnectedAtRef = useRef<number | null>(null);
  
  // Mantém ref sincronizada com state para uso em event handlers
  useEffect(() => {
    currentCallRecordIdRef.current = currentCallRecordId;
  }, [currentCallRecordId]);

  useEffect(() => {
    currentCallRef.current = currentCall;
  }, [currentCall]);

  // Rede de segurança: o SDK do Twilio pode chamar replaceTrack numa peer connection
  // já fechada durante reconexão (deviceChange -> setInputDevice), gerando uma
  // rejeição não tratada (InvalidStateError). Isso é ruído, não falha real.
  useEffect(() => {
    const isTwilioTrackRaceError = (reason: unknown) => {
      const name = (reason as { name?: string } | null)?.name;
      const message = String((reason as { message?: string } | null)?.message ?? reason ?? "");
      return (
        name === "InvalidStateError" &&
        (message.includes("replaceTrack") || message.includes("peer connection is closed"))
      );
    };

    const handler = (event: PromiseRejectionEvent) => {
      if (isTwilioTrackRaceError(event.reason)) {
        console.warn("[Twilio] Ignorando InvalidStateError de replaceTrack durante reconexão");
        event.preventDefault();
      }
    };

    window.addEventListener("unhandledrejection", handler);
    return () => window.removeEventListener("unhandledrejection", handler);
  }, []);


  // Função helper para traduzir códigos de erro Twilio para mensagens em português
  const getErrorMessageInPortuguese = (code: number, message: string): string => {
    const errorMap: Record<number, string> = {
      10004: 'Sistema no limite de chamadas simultâneas. Aguarde alguns segundos e tente novamente.',
      31005: 'Falha ao conectar com o gateway de chamadas. Verifique sua conexão.',
      31486: 'Número ocupado ou não disponível. Tente novamente mais tarde.',
      31000: 'Falha na autenticação. Recarregue a página.',
      31201: 'Timeout ao conectar. Verifique sua internet.',
      31204: 'Número inválido. Verifique o formato do telefone.',
      31206: 'Sem saldo na conta Twilio. Entre em contato com o administrador.',
      53000: 'Sem permissão para fazer chamadas. Entre em contato com o administrador.',
      53001: 'Chamadas para este país não estão habilitadas. Entre em contato com o administrador.',
    };
    
    return errorMap[code] || `Erro na chamada: ${message}`;
  };

  // Prevent page refresh/close during active calls AND ensure finalization
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isOnCall || isInitiatingCall) {
        e.preventDefault();
        e.returnValue = "Você está em uma chamada ativa. Tem certeza que deseja sair?";
        
        // Tenta finalizar a chamada usando sendBeacon (não bloqueia)
        if (currentCallRecordIdRef.current) {
          // Marca no localStorage para cleanup quando voltar
          localStorage.setItem('pendingCallFinalization', currentCallRecordIdRef.current);
        }
        
        return e.returnValue;
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isOnCall, isInitiatingCall]);

  // Visibility change handler - detecta quando aba é fechada/minimizada
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden' && currentCallRecordIdRef.current) {
        // Marca chamada para cleanup quando voltar
        localStorage.setItem('pendingCallFinalization', currentCallRecordIdRef.current);
        console.log('📌 Marked call for finalization on visibility change:', currentCallRecordIdRef.current);
      } else if (document.visibilityState === 'visible') {
        // Quando aba volta a ficar visível, verifica se há chamadas pendentes
        const pendingId = localStorage.getItem('pendingCallFinalization');
        if (pendingId && !isOnCall && !isInitiatingCall) {
          console.log('🔧 Finalizing pending call from visibility change:', pendingId);
          ensureCallFinalized(pendingId);
          localStorage.removeItem('pendingCallFinalization');
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [ensureCallFinalized, isOnCall, isInitiatingCall]);

  // Cleanup de chamadas pendentes ao iniciar o app
  useEffect(() => {
    if (!user?.id) return;
    
    // Verifica se há chamada pendente de sessão anterior
    const pendingId = localStorage.getItem('pendingCallFinalization');
    if (pendingId) {
      console.log('🧹 Found pending call from previous session:', pendingId);
      ensureCallFinalized(pendingId);
      localStorage.removeItem('pendingCallFinalization');
    }
    
    // Limpa chamadas antigas do usuário
    cleanupStaleCalls(user.id);
  }, [user?.id, ensureCallFinalized, cleanupStaleCalls]);

  // Handler global para AbortError de áudio do Twilio SDK
  useEffect(() => {
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      // Promise rejected with undefined/null - Twilio SDK internal behavior on disconnect
      if (event.reason === undefined || event.reason === null) {
        console.log('[TwilioContext] Promise rejected with undefined/null - suppressing (Twilio SDK)');
        event.preventDefault();
        return;
      }
      // AbortError de audio.play() quando chamada é desconectada - comportamento esperado
      if (event.reason?.name === 'AbortError') {
        console.log('[TwilioContext] Audio playback cancelled - call ended (expected behavior)');
        event.preventDefault();
        return;
      }
    };
    
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    
    return () => {
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  useEffect(() => {
    // Aguarda estado de auth e só inicializa se usuário está logado
    if (loading || !user || !session) {
      return;
    }

    const initializeDevice = async () => {
      try {
        setIsConnecting(true);
        setError(null);

        console.log("Fetching Twilio token...");
        const { data, error: tokenError } = await supabase.functions.invoke("twilio-token");

        if (tokenError) throw tokenError;
        if (!data?.token) throw new Error("Failed to get Twilio token");

        console.log("Twilio token received, initializing device...");
        console.log("Outgoing number:", data.outgoingNumber);

        const twilioDevice = new Device(data.token, {
          logLevel: 1,
          codecPreferences: [Call.Codec.Opus, Call.Codec.PCMU],
        });

        // Event listeners
        twilioDevice.on("registered", async () => {
          console.log("Twilio device registered and ready");

          // Clear registration retry timeout
          if (registrationTimeoutRef.current) {
            clearTimeout(registrationTimeoutRef.current);
            registrationTimeoutRef.current = null;
          }
          retryCountRef.current = 0;

          // Only run cleanup and reset on FIRST registration
          // Subsequent re-registrations (e.g., after token refresh) should NOT clear active call state
          if (!hasRegisteredOnce.current) {
            console.log("🔵 First registration - running cleanup");

            // Reset all call states on device registration
            setIsOnCall(false);
            setCurrentCall(null);
            setIsInitiatingCall(false);
            setCurrentCallTargetNumber(null);
            setCurrentCallRecordId(null);
            setIncomingCall(null);

            // Clean up only "initiating" calls (not "in-progress" to avoid killing active calls)
            try {
              const { error: cleanupError } = await supabase
                .from("calls")
                .update({
                  status: "failed",
                  ended_at: new Date().toISOString(),
                })
                .eq("status", "initiating") // Only initiating, not in-progress
                .eq("created_by", user.id)
                .gte("created_at", new Date(Date.now() - 5 * 60 * 1000).toISOString());

              if (cleanupError) {
                console.error("Error cleaning up stale calls:", cleanupError);
              } else {
                console.log("✅ Cleaned up stale initiating calls");
              }
            } catch (err) {
              console.error("Error during call cleanup:", err);
            }

          hasRegisteredOnce.current = true;
        } else {
            console.log("🔄 Re-registration detected - preserving call state");
          }

          setIsReady(true);
          setIsConnecting(false);
          setOutgoingNumber(data.outgoingNumber || null);

          // Fetch and cache the app_user id for call attribution
          if (user?.id) {
            const { data: appUser } = await supabase
              .from('app_users')
              .select('id')
              .eq('auth_user_id', user.id)
              .single();
            if (appUser) {
              setCurrentAppUserId(appUser.id);
              console.log('📋 [TwilioContext] Cached app_user_id:', appUser.id);
            }
          }
        });

        // Re-registro centralizado com debounce e respeito à reconexão automática do SDK
        const attemptReRegistration = async (origin: string) => {
          // Se existe chamada ativa, o próprio SDK reconecta o transporte — não interferir
          if (currentCallRef.current) {
            console.log(`⏭️ Re-registro (${origin}) ignorado: chamada ativa, SDK reconecta sozinho`);
            return;
          }

          const now = Date.now();
          if (now - lastReRegisterAtRef.current < REREGISTER_DEBOUNCE_MS) {
            console.log(`⏭️ Re-registro (${origin}) ignorado: tentativa recente em andamento`);
            return;
          }
          lastReRegisterAtRef.current = now;

          try {
            console.log(`🔄 Attempting re-registration (${origin})...`);
            const { data: freshData } = await supabase.functions.invoke("twilio-token");
            if (freshData?.token) {
              twilioDevice.updateToken(freshData.token);
            }
            await twilioDevice.register();
          } catch (retryErr) {
            const name = (retryErr as { name?: string } | null)?.name;
            if (name === "InvalidStateError") {
              console.warn(`Re-registro (${origin}): InvalidStateError ignorado`);
              return;
            }
            console.error(`Re-registration (${origin}) failed:`, retryErr);
          }
        };

        twilioDevice.on("error", (err) => {
          console.error("Twilio device error:", err);
          setError(err.message || "Device error");
          setIsConnecting(false);
          // Don't block permanently - attempt re-registration after 5s
          setTimeout(() => {
            void attemptReRegistration("error");
          }, 5000);
        });

        twilioDevice.on("unregistered", async () => {
          console.warn("⚠️ Twilio device unregistered, attempting re-registration...");
          setIsReady(false);
          await attemptReRegistration("unregistered");
        });


        // Token renewal - critical for stability
        twilioDevice.on("tokenWillExpire", async () => {
          console.log("🔄 Twilio token will expire, refreshing...");
          try {
            const { data: tokenData, error: tokenError } = await supabase.functions.invoke("twilio-token");

            if (tokenError) throw tokenError;
            if (!tokenData?.token) throw new Error("Failed to refresh token");

            twilioDevice.updateToken(tokenData.token);
            console.log("✅ Token refreshed successfully");
          } catch (err) {
            console.error("❌ Error refreshing token:", err);
            toast.error("Erro ao renovar conexão de voz");
          }
        });

        twilioDevice.on("tokenExpired", async () => {
          console.log("⚠️ Twilio token expired, attempting recovery...");
          try {
            const { data: tokenData, error: tokenError } = await supabase.functions.invoke("twilio-token");

            if (tokenError) throw tokenError;
            if (!tokenData?.token) throw new Error("Failed to get new token");

            twilioDevice.updateToken(tokenData.token);
            await twilioDevice.register();
            console.log("✅ Recovered from token expiration");
            toast.info("Conexão de voz restabelecida");
          } catch (err) {
            console.error("❌ Failed to recover from token expiration:", err);
            toast.error("Falha crítica no sistema de voz");
          }
        });

        twilioDevice.on("incoming", (call) => {
          console.log("📞 INCOMING CALL DETECTED!", {
            from: call.parameters.From,
            to: call.parameters.To,
            callSid: call.parameters.CallSid,
          });

          // Auto-aceitar se está fazendo pickup da fila
          if (isPickingUpRef.current) {
            console.log("🎯 Auto-accepting pickup call from queue");
            acceptIncomingCall(call);
            setIsPickingUp(false);
            setIsParked(false);
            setParkedQueue(null);
            return;
          }

          // Adicionar listeners imediatamente para detectar cancelamento
          call.on("cancel", () => {
            console.log("📵 Incoming call CANCELLED by caller before answer");

            // Fechar modal imediatamente
            setIncomingCall(null);

            // ATUALIZAR registro existente ao invés de inserir novo
            (async () => {
              try {
                const { error: updateError } = await supabase
                  .from("calls")
                  .update({
                    status: "no-answer",
                    ended_at: new Date().toISOString(),
                  })
                  .eq("twilio_call_sid", call.parameters.CallSid);

                if (updateError) {
                  console.error("Error updating missed call:", updateError);
                } else {
                  console.log("✅ Missed call status updated successfully");
                }
              } catch (err) {
                console.error("Error updating missed call:", err);
              }
            })();

            toast.info(`Missed call from ${call.parameters.From}`);
          });

          call.on("disconnect", () => {
            console.log("📵 Incoming call DISCONNECTED before answer");
            setIncomingCall(null);
          });

          call.on("reject", () => {
            console.log("📵 Incoming call REJECTED by user");
            setIncomingCall(null);
          });

          setIncomingCall(call);
          toast.info("Chamada entrante!");
        });

        await twilioDevice.register();
        setDevice(twilioDevice);

        // Start registration timeout - retry if registered event doesn't fire
        registrationTimeoutRef.current = setTimeout(async () => {
          if (!hasRegisteredOnce.current && retryCountRef.current < MAX_REGISTRATION_RETRIES) {
            retryCountRef.current++;
            console.warn(`⏱️ Twilio registration timeout - retry ${retryCountRef.current}/${MAX_REGISTRATION_RETRIES}`);
            try {
              const { data: newData } = await supabase.functions.invoke("twilio-token");
              if (newData?.token) {
                twilioDevice.updateToken(newData.token);
              }
              await twilioDevice.register();
            } catch (retryErr) {
              console.error("Registration retry failed:", retryErr);
            }
          }
        }, 15000);
      } catch (err) {
        console.error("Error initializing Twilio device:", err);
        setError(err instanceof Error ? err.message : "Failed to initialize device");
        setIsConnecting(false);
        toast.error("Erro ao inicializar sistema de chamadas");
      }
    };

    initializeDevice();

    return () => {
      // Clear registration retry timeout
      if (registrationTimeoutRef.current) {
        clearTimeout(registrationTimeoutRef.current);
        registrationTimeoutRef.current = null;
      }

      if (device) {
        console.log("Cleaning up Twilio device...");

        // Reset all call states
        setIsOnCall(false);
        setCurrentCall(null);
        setIsInitiatingCall(false);
        setCurrentCallTargetNumber(null);
        setCurrentCallRecordId(null);
        setIncomingCall(null);

        // Disconnect any active calls before destroying device
        if (device.calls && device.calls.length > 0) {
          device.calls.forEach((call) => {
            try {
              call.disconnect();
            } catch (err) {
              console.error("Error disconnecting call during cleanup:", err);
            }
          });
        }

        device.unregister();
        device.destroy();
      }
    };
  }, [loading, user?.id, session?.access_token]);

  const makeCall = useCallback(
    async (phoneNumber: string, contactId?: string, leadId?: string) => {
      if (!device || !isReady) {
        throw new Error("Device not ready");
      }
      setIsDialerCall(false);

      // Guard against multiple simultaneous calls.
      // Não lançamos erro aqui: é comportamento esperado (clique duplo) e
      // gerava ruído no Sentry via unhandled rejection. Retorna null.
      if (currentCall || isInitiatingCall) {
        console.warn("⚠️ Call already in progress, ignoring duplicate request");
        toast.warning("Já há uma chamada em andamento");
        return null;
      }


      // Verifica permissão de microfone antes de iniciar
      try {
        const permissionResult = await navigator.permissions.query({ name: 'microphone' as PermissionName });
        if (permissionResult.state === 'denied') {
          console.log('🎤 Microphone permission denied, dispatching event');
          // Dispara evento para mostrar o modal de permissão
          window.dispatchEvent(new CustomEvent('microphone-permission-denied'));
          toast.error('Permissão de microfone negada. Clique no cadeado 🔒 na barra de endereço para permitir.');
          throw new Error('Microphone permission denied');
        }
      } catch (permErr) {
        // Se o navegador não suporta permissions API, tenta seguir normalmente
        // O erro será capturado pelo Twilio depois
        if ((permErr as Error).message === 'Microphone permission denied') {
          throw permErr;
        }
        console.log('🎤 Permissions API not available, proceeding with call');
      }

      try {
        setIsInitiatingCall(true);
        setIsConnecting(true);
        setCurrentCallTargetNumber(phoneNumber);
        setCurrentConsultantId(contactId || null);
        setCallError(null); // Limpar erro anterior

        // Se temos leadId mas não contactId, procura se existe contact com mesmo número
        let finalContactId = contactId;
        if (leadId && !contactId) {
          const { data: lead } = await supabase
            .from("leads")
            .select("contact_id, primary_phone")
            .eq("id", leadId)
            .single();

          if (lead?.contact_id) {
            finalContactId = lead.contact_id;
          } else if (lead?.primary_phone) {
            // Normaliza o número para busca flexível
            const normalizedPhone = lead.primary_phone.replace(/[^\d]/g, '').slice(-10);
            
            // Primeiro tenta match exato
            const { data: exactContact } = await supabase
              .from("contacts")
              .select("id")
              .eq("primary_phone", lead.primary_phone)
              .maybeSingle();

            if (exactContact) {
              finalContactId = exactContact.id;
              console.log("Found existing contact with exact phone match:", exactContact.id);
            } else {
              // Busca flexível - busca contatos e compara últimos 10 dígitos
              const { data: allContacts } = await supabase
                .from("contacts")
                .select("id, primary_phone")
                .not("primary_phone", "is", null);
              
              const matchingContact = allContacts?.find(c => {
                const contactNormalized = c.primary_phone?.replace(/[^\d]/g, '').slice(-10);
                return contactNormalized === normalizedPhone;
              });
              
              if (matchingContact) {
                finalContactId = matchingContact.id;
                console.log("Found existing contact with flexible phone match:", matchingContact.id);
              }
            }
          }
        }

        // Cria o registro no banco (necessário para tracking)
        const { data: callRecord, error: insertError } = await supabase
          .from("calls")
          .insert([{
            contact_id: finalContactId || null,
            lead_id: leadId || null,
            direction: "outbound",
            from_e164: outgoingNumber,
            to_e164: phoneNumber,
            status: "initiating",
            provider: "twilio",
            channel: "voice",
            handled_by_app_user_id: currentAppUserId,
          }])
          .select()
          .single();

        if (insertError || !callRecord) {
          throw insertError || new Error("Failed to create call record");
        }

        // Inicia a chamada imediatamente
        const call = await device.connect({
          params: {
            To: phoneNumber,
            targetPhone: phoneNumber,
            direction: "outbound",
            callRecordId: callRecord.id,
          },
        });

        // Adiciona direção como custom parameter
        (call as any).customParameters = new Map([["direction", "outbound"]]);

        console.log('📞 [TwilioContext] Call connecting:', { 
          callRecordId: callRecord.id,
          hasCallSid: !!call.parameters.CallSid,
        });

        // Store call record ID for transfer functionality
        setCurrentCallRecordId(callRecord.id);

        // CallSid não está disponível neste momento - será capturado no evento "ringing"
        if (!call.parameters.CallSid) {
          console.log('📋 [TwilioContext] CallSid not yet available (expected, will capture on ringing)');
        }

        // Salva CallSid no evento ringing (quando fica disponível)
        call.on("ringing", (hasEarlyMedia: boolean) => {
          const ringingCallSid = call.parameters.CallSid;
          console.log("🔔 [TwilioContext] Call ringing:", {
            callSid: ringingCallSid,
            hasEarlyMedia,
            callRecordId: callRecord.id
          });
          
          if (ringingCallSid) {
            supabase
              .from("calls")
              .update({ twilio_call_sid: ringingCallSid })
              .eq("id", callRecord.id)
              .is("twilio_call_sid", null) // Só atualiza se ainda não foi salvo
              .then(({ error }) => {
                if (error) console.error("Failed to save CallSid on ringing:", error);
                else console.log("✅ CallSid saved on ringing event");
              });
          }
        });

        let hasBeenAccepted = false;

        call.on("accept", async () => {
          console.log("✅ Call accepted");
          hasBeenAccepted = true;
          callConnectedAtRef.current = Date.now();

          // FASE 2: Verifica se CallSid foi salvo, senão tenta novamente
          const { data: currentCall } = await supabase
            .from("calls")
            .select("twilio_call_sid")
            .eq("id", callRecord.id)
            .single();
            
          if (!currentCall?.twilio_call_sid && call.parameters.CallSid) {
            console.log("⚠️ [TwilioContext] CallSid missing on accept, saving now:", call.parameters.CallSid);
            const { error } = await supabase
              .from("calls")
              .update({ twilio_call_sid: call.parameters.CallSid })
              .eq("id", callRecord.id);
              
            if (error) {
              console.error("❌ [TwilioContext] Failed to save CallSid on accept:", error);
            } else {
              console.log("✅ [TwilioContext] Successfully saved CallSid on accept");
            }
          }

          // Update assíncrono - não bloqueia. Usa .is() para prevenir sobrescrever se webhook já definiu
          supabase
            .from("calls")
            .update({ status: "in-progress", started_at: new Date().toISOString() })
            .eq("id", callRecord.id)
            .is("started_at", null) // Previne sobrescrever se webhook já definiu
            .select()
            .then(({ data, error }) => {
              if (error) {
                console.error("Error updating call status:", error);
              } else if (!data || data.length === 0) {
                console.log("ℹ️ [TwilioContext] started_at already set (possibly by webhook)");
              }
            });

          setIsOnCall(true);
          setIsConnecting(false);
          setIsInitiatingCall(false);
          setCurrentCall(call);
        });

        call.on("disconnect", async () => {
          console.log("📴 Call disconnected");

          // Detecta instant disconnect (chamada rejeitada pela operadora)
          if (hasBeenAccepted && callConnectedAtRef.current) {
            const callDuration = Date.now() - callConnectedAtRef.current;
            console.log(`📊 Call duration: ${callDuration}ms`);
            if (callDuration < 3000) {
              console.log("⚡ Instant disconnect detected - carrier rejection");
              setInstantDisconnect(true);
            }
            callConnectedAtRef.current = null;
          }

          // Se foi desconexão causada por PARK, manter UI ativa e NÃO finalizar no banco
          if (isParkedRef.current) {
            console.log("🅿️ Disconnect due to park: preserving UI state");
            setCurrentCall(null);
            setIsConnecting(false);
            setIsInitiatingCall(false);
            return;
          }

          // Busca estado atual ANTES de atualizar
          const { data: currentCallData } = await supabase
            .from("calls")
            .select("started_at, duration_seconds, twilio_call_sid, status, ended_at")
            .eq("id", callRecord.id)
            .single();

          // Verifica se webhook já finalizou a chamada
          if (['completed', 'no-answer', 'busy', 'failed'].includes(currentCallData?.status)) {
            console.log("✅ [TwilioContext] Webhook already finalized call:", {
              callId: callRecord.id,
              webhookStatus: currentCallData.status,
              webhookEndedAt: currentCallData.ended_at,
              webhookDuration: currentCallData.duration_seconds
            });
            
            // Apenas limpa estados locais, NÃO atualiza banco
            setIsOnCall(false);
            setCurrentCall(null);
            setCurrentCallTargetNumber(null);
            setIsConnecting(false);
            
            if (!hasBeenAccepted) {
              setTimeout(() => setIsInitiatingCall(false), 3000);
            } else {
              setIsInitiatingCall(false);
            }
            return;
          }

          // Se não tem duração e tem started_at, calcula manualmente
          if (!currentCallData?.duration_seconds && currentCallData?.started_at) {
            const startTime = new Date(currentCallData.started_at).getTime();
            const endTime = new Date().getTime();
            const calculatedDuration = Math.floor((endTime - startTime) / 1000);
            
            if (calculatedDuration > 0) {
              console.log(`⚠️ [TwilioContext] Webhook didn't set duration, calculating: ${calculatedDuration}s`);
            }
          }
          
          // Se twilio_call_sid ainda está null, tenta salvar uma última vez
          if (!currentCallData?.twilio_call_sid && call.parameters.CallSid) {
            console.log(`⚠️ [TwilioContext] CallSid still missing on disconnect, saving: ${call.parameters.CallSid}`);
            await supabase
              .from("calls")
              .update({ twilio_call_sid: call.parameters.CallSid })
              .eq("id", callRecord.id);
          }

          // Se a chamada nunca foi aceita (rejected/failed), marca como failed
          const finalStatus = hasBeenAccepted ? "completed" : "failed";

          // Update assíncrono - não bloqueia
          supabase
            .from("calls")
            .update({ status: finalStatus, ended_at: new Date().toISOString() })
            .eq("id", callRecord.id)
            .then(({ error }) => {
              if (error) console.error("Error updating call status:", error);
            });

          // Auto-completa agendamentos pendentes para este contato
          if (hasBeenAccepted && finalContactId) {
            const now = new Date();
            const windowStart = new Date(now.getTime() - 24 * 60 * 60 * 1000).toISOString();
            const windowEnd = new Date(now.getTime() + 2 * 60 * 60 * 1000).toISOString();
            supabase
              .from("calls")
              .update({
                status: "completed",
                completed_at: now.toISOString(),
                ended_at: now.toISOString(),
              })
              .eq("contact_id", finalContactId)
              .eq("status", "scheduled")
              .gte("scheduled_for", windowStart)
              .lte("scheduled_for", windowEnd)
              .then(({ error }) => {
                if (error) console.error("[TwilioContext] Error auto-completing scheduled call:", error);
                else console.log("[TwilioContext] Auto-completed scheduled call for contact:", finalContactId);
              });
          }

          // Se nunca foi aceita, mostra feedback
          if (!hasBeenAccepted) {
            if (callError) {
              const friendlyMessage = getErrorMessageInPortuguese(
                callError.code,
                callError.message
              );
              toast.error(friendlyMessage);
            } else {
              toast.error("Chamada não completada");
            }
            // Manter modal aberto por 3s para mostrar erro
            setTimeout(() => {
              setIsInitiatingCall(false);
            }, 3000);
          } else {
            setIsInitiatingCall(false);
          }

          setIsOnCall(false);
          setCurrentCall(null);
          setCurrentCallTargetNumber(null);
          // Não limpa currentCallRecordId aqui - precisa ficar disponível para salvar notas
          setIsConnecting(false);
        });

        call.on("cancel", () => {
          console.log("Call cancelled");
          setIsOnCall(false);
          setCurrentCall(null);
          setIsConnecting(false);
          setIsInitiatingCall(false);
          setCurrentCallTargetNumber(null);
          setCurrentCallRecordId(null);
        });

        call.on("error", (error: any) => {
          console.error("❌ Call error:", error);
          
          const errorCode = error.code || 0;
          const errorMessage = error.message || "Unknown error";
          
          setCallError({
            code: errorCode,
            message: errorMessage,
          });
          
          // Log detalhado para debugging
          console.log("Call error details:", {
            code: errorCode,
            message: errorMessage,
            twilioError: error.twilioError,
            causes: error.causes,
          });
        });

        return call;
      } catch (err) {
        console.error("❌ Error making call:", err);
        
        // Se já criou o registro, marca como failed
        const pendingCallId = currentCallRecordIdRef.current;
        if (pendingCallId) {
          console.log("🔧 Marking failed call in DB:", pendingCallId);
          supabase
            .from("calls")
            .update({ status: "failed", ended_at: new Date().toISOString() })
            .eq("id", pendingCallId)
            .then(({ error }) => {
              if (error) console.error("Error marking call as failed:", error);
            });
        }
        
        setIsOnCall(false);
        setCurrentCall(null);
        setCurrentCallTargetNumber(null);
        setCurrentCallRecordId(null);
        setError(err instanceof Error ? err.message : "Failed to make call");
        setIsConnecting(false);
        setIsInitiatingCall(false);
        
        // Mostrar toast com erro específico
        const errorMessage = err instanceof Error ? err.message : "Falha ao iniciar chamada";
        toast.error(errorMessage);
        
        throw err;
      }
    },
    [device, isReady, outgoingNumber, currentAppUserId],
  );

  // Função especial para chamadas a consultores (internas)
  const makeConsultantCall = useCallback(
    async (phoneNumber: string, consultantUserId: string): Promise<Call> => {
      if (!device || !isReady) {
        throw new Error("Device not ready");
      }

      try {
        setIsConnecting(true);
        setIsInitiatingCall(true);
        setCallError(null);
        setCurrentCallTargetNumber(phoneNumber);
        setCurrentConsultantId(consultantUserId);

        // Cria registro no banco antes de iniciar a chamada
        const { data: callRecord, error: insertError } = await supabase
          .from("calls")
          .insert([{
            direction: "outbound",
            from_e164: outgoingNumber,
            to_e164: phoneNumber,
            status: "initiating",
            provider: "twilio",
            channel: "voice",
            handled_by_app_user_id: consultantUserId, // Consultor que está sendo chamado
          }])
          .select()
          .single();

        if (insertError || !callRecord) {
          throw insertError || new Error("Failed to create call record");
        }

        console.log("Created call record for consultant:", callRecord.id);
        setCurrentCallRecordId(callRecord.id);

        const params = {
          To: phoneNumber,
          targetPhone: phoneNumber,
          direction: `outbound-consultant:${phoneNumber}`,
          callRecordId: callRecord.id,
        };

        console.log("Making consultant call with params:", params);
        console.log("Consultant phone number:", phoneNumber);
        console.log("Current parked client:", { parkedClientName, parkedClientPhone });
        const call = await device.connect({ params });

        const callSid = call.parameters.CallSid;
        console.log('📞 [Consultant Call] Connecting:', { 
          callSid, 
          callRecordId: callRecord.id,
          hasCallSid: !!callSid
        });

        // Salva CallSid assim que disponível
        if (callSid) {
          supabase
            .from("calls")
            .update({ twilio_call_sid: callSid })
            .eq("id", callRecord.id)
            .then(({ error }) => {
              if (error) console.error("Failed to save CallSid:", error);
              else console.log("✅ CallSid saved:", callSid);
            });
        }

        call.on("accept", async () => {
          console.log("Consultant call accepted");
          
          // Atualiza status no banco
          await supabase
            .from("calls")
            .update({ status: "in-progress", started_at: new Date().toISOString() })
            .eq("id", callRecord.id);
          
          setIsOnCall(true);
          setIsConnecting(false);
          setIsInitiatingCall(false);
        });

        call.on("disconnect", async () => {
          console.log("Consultant call disconnected");
          
          // Atualiza status no banco
          await supabase
            .from("calls")
            .update({ status: "completed", ended_at: new Date().toISOString() })
            .eq("id", callRecord.id);
          
          setIsOnCall(false);
          setCurrentCall(null);
          setCurrentCallTargetNumber(null);
          setCurrentConsultantId(null);
          setCurrentCallRecordId(null);
          setIsConnecting(false);
          setIsInitiatingCall(false);
        });

        call.on("error", (error: any) => {
          console.error("Consultant call error:", error);
          const errorCode = error.code || 0;
          const errorMessage = error.message || "Unknown error";
          
          setCallError({
            code: errorCode,
            message: errorMessage,
          });
        });

        setCurrentCall(call);
        return call;
      } catch (err) {
        console.error("Error making consultant call:", err);
        setIsOnCall(false);
        setCurrentCall(null);
        setCurrentCallTargetNumber(null);
        setCurrentConsultantId(null);
        setError(err instanceof Error ? err.message : "Failed to make consultant call");
        setIsConnecting(false);
        setIsInitiatingCall(false);
        throw err;
      }
    },
    [device, isReady, outgoingNumber],
  );

  // Ring All - toca para todos consultores disponíveis simultaneamente
  const makeRingAllCall = useCallback(
    async (): Promise<Call> => {
      if (!device || !isReady) {
        throw new Error("Device not ready");
      }

      try {
        setIsConnecting(true);
        setIsInitiatingCall(true);
        setCallError(null);

        console.log("Starting Ring All call to all available consultants");

        const params = {
          direction: "outbound-ring-all",
          callType: "ring-all",
        };

        console.log("Making Ring All call with params:", params);
        const call = await device.connect({ params });

        const callSid = call.parameters.CallSid;
        console.log('📞 [Ring All Call] Connecting:', { callSid });

        call.on("accept", async () => {
          console.log("✅ Ring All call - consultant answered");
          setIsOnCall(true);
          setIsConnecting(false);
          setIsInitiatingCall(false);
          toast.success("Consultor atendeu!");
        });

        call.on("disconnect", async () => {
          console.log("Ring All call disconnected");
          setIsOnCall(false);
          setCurrentCall(null);
          setCurrentCallTargetNumber(null);
          setCurrentConsultantId(null);
          setCurrentCallRecordId(null);
          setIsConnecting(false);
          setIsInitiatingCall(false);
        });

        call.on("error", (error: any) => {
          console.error("Ring All call error:", error);
          const errorCode = error.code || 0;
          const errorMessage = error.message || "Unknown error";
          
          setCallError({
            code: errorCode,
            message: errorMessage,
          });
        });

        setCurrentCall(call);
        return call;
      } catch (err) {
        console.error("Error making Ring All call:", err);
        setIsOnCall(false);
        setCurrentCall(null);
        setError(err instanceof Error ? err.message : "Failed to make Ring All call");
        setIsConnecting(false);
        setIsInitiatingCall(false);
        throw err;
      }
    },
    [device, isReady],
  );

  /**
   * Power Dialer: conecta o SDR na conferência que receberá as ligações
   * humanas detectadas pelo AMD.
   */
  const joinDialerConference = useCallback(
    async (conferenceName: string): Promise<Call | null> => {
      if (!device || !isReady) {
        toast.error("Telefone não está pronto");
        return null;
      }
      if (currentCall || isInitiatingCall) {
        toast.warning("Já há uma chamada em andamento");
        return null;
      }

      try {
        // Preflight explícito: o Power Dialer não origina contatos se o
        // navegador não conseguir abrir o microfone do SDR.
        const microphone = await navigator.mediaDevices.getUserMedia({ audio: true });
        microphone.getTracks().forEach((track) => track.stop());

        setIsConnecting(true);
        setIsInitiatingCall(true);
        setIsDialerCall(true);
        setCallError(null);

        const call = await device.connect({
          params: {
            dialerConference: conferenceName,
            direction: "power-dialer-agent",
          },
        });

        setCurrentCall(call);

        const accepted = await new Promise<boolean>((resolve) => {
          let settled = false;
          const finish = (value: boolean) => {
            if (settled) return;
            settled = true;
            resolve(value);
          };
          const timeout = window.setTimeout(() => finish(false), 15000);

          call.on("accept", () => {
            window.clearTimeout(timeout);
            setIsOnCall(true);
            setIsConnecting(false);
            setIsInitiatingCall(false);
            setIsDialerAudioReady(true);
            finish(true);
          });
          call.on("error", (err: any) => {
            window.clearTimeout(timeout);
            console.error("Power Dialer conference error:", err);
            setCallError({ code: err?.code || 0, message: err?.message || "Erro na conferência" });
            finish(false);
          });
        });

        call.on("disconnect", () => {
          setIsOnCall(false);
          setCurrentCall(null);
          setIsConnecting(false);
          setIsInitiatingCall(false);
          setIsDialerCall(false);
          setIsDialerAudioReady(false);
        });

        if (!accepted) {
          call.disconnect();
          setCurrentCall(null);
          setIsDialerAudioReady(false);
          toast.error("Não foi possível abrir o áudio do Power Dialer");
          return null;
        }
        return call;
      } catch (err) {
        console.error("Error joining dialer conference:", err);
        const name = (err as any)?.name as string | undefined;
        if (name === "NotAllowedError" || name === "SecurityError") {
          toast.error(
            "Microfone bloqueado pelo navegador. Abra o app em uma aba própria (não dentro do preview) e permita o microfone.",
            { duration: 8000 },
          );
        } else if (name === "NotFoundError" || name === "NotReadableError") {
          toast.error("Nenhum microfone disponível ou ele está em uso por outro aplicativo.");
        } else {
          toast.error((err as Error)?.message || "Não foi possível abrir o áudio do Power Dialer");
        }
        setIsConnecting(false);
        setIsInitiatingCall(false);
        setIsDialerCall(false);
        setIsDialerAudioReady(false);
        setCurrentCall(null);
        return null;
      }
    },
    [device, isReady, currentCall, isInitiatingCall],
  );



  const acceptIncomingCall = useCallback(async (call: Call) => {
    try {
      console.log("Accepting incoming call...", { from: call.parameters.From });
      setCallError(null); // Limpar erro anterior

      // Aceita a chamada imediatamente para reduzir latência
      call.accept();

      // Adiciona direção como custom parameter
      (call as any).customParameters = new Map([["direction", "inbound"]]);

      // Atualiza UI imediatamente
      setIsOnCall(true);
      setCurrentCall(call);
      setIncomingCall(null);
      setCurrentCallTargetNumber(call.parameters.From);

      // Busca o contato e cria o registro de forma assíncrona (não bloqueia)
      (async () => {
        const { data: contact } = await supabase
          .from("contacts")
          .select("id")
          .eq("primary_phone", call.parameters.From)
          .single();

        console.log("Found contact for incoming call:", contact);

        const { data: callRecord, error: insertError } = await supabase
          .from("calls")
          .insert([{
            direction: "inbound",
            from_e164: call.parameters.From,
            to_e164: call.parameters.To,
            status: "in-progress",
            started_at: new Date().toISOString(),
            twilio_call_sid: call.parameters.CallSid,
            provider: "twilio",
            contact_id: contact?.id || null,
            channel: "voice",
          }])
          .select()
          .single();

        if (insertError) {
          console.error("Error creating call record:", insertError);
        } else if (callRecord) {
          // Armazena o ID do registro para poder salvar notas depois
          setCurrentCallRecordId(callRecord.id);
        }

        // Configura o listener de disconnect com o callRecord
        call.on("disconnect", () => {
          console.log("Incoming call disconnected");

          // Se foi desconexão causada por PARK, manter UI ativa e NÃO finalizar no banco
          if (isParkedRef.current) {
            console.log("🅿️ Incoming disconnect due to park: preserving UI state");
            setCurrentCall(null);
            setIsConnecting(false);
            return;
          }

          if (callRecord) {
            supabase
              .from("calls")
              .update({ status: "completed", ended_at: new Date().toISOString() })
              .eq("id", callRecord.id)
              .then(({ error }) => {
                if (error) console.error("Error updating call status:", error);
              });
          }

          setIsOnCall(false);
          setCurrentCall(null);
          setIncomingCall(null);
          setCurrentCallTargetNumber(null);
          // Não limpa currentCallRecordId aqui - precisa ficar disponível para salvar notas
          setIsConnecting(false);
        });
      })();
    } catch (err) {
      console.error("Error accepting call:", err);
      setIncomingCall(null);
    }
  }, []);

  const rejectIncomingCall = useCallback(async (call: Call) => {
    try {
      console.log("Rejecting incoming call...");

      // Atualiza status no banco de dados antes de rejeitar
      const twilioCallSid = call.parameters.CallSid;

      if (twilioCallSid) {
        const { error: updateError } = await supabase
          .from("calls")
          .update({
            status: "no-answer",
            ended_at: new Date().toISOString(),
          })
          .eq("twilio_call_sid", twilioCallSid);

        if (updateError) {
          console.error("Error updating rejected call status:", updateError);
        }
      }

      // Rejeita a chamada no Twilio
      call.reject();
      setIncomingCall(null);
    } catch (err) {
      console.error("Error rejecting call:", err);
      call.reject();
      setIncomingCall(null);
    }
  }, []);

  const hangUp = useCallback(() => {
    if (currentCall) {
      currentCall.disconnect();
    }
  }, [currentCall]);

  const toggleMute = useCallback(() => {
    if (currentCall) {
      const isMuted = currentCall.isMuted();
      currentCall.mute(!isMuted);
      return !isMuted;
    }
    return false;
  }, [currentCall]);

  const sendDtmf = useCallback((digit: string) => {
    if (currentCall) {
      console.log(`📞 Sending DTMF tone: ${digit}`);
      currentCall.sendDigits(digit);
    }
  }, [currentCall]);

  // Cold transfer - comentado temporariamente (não usado atualmente)
  const transferCall = useCallback(async (destination: string, destinationUserId?: string) => {
    toast.error("Funcionalidade de transferência desabilitada");
    console.log("Transfer not implemented:", { destination, destinationUserId });
  }, []);

  const holdCall = useCallback(async () => {
    if (!currentCall || isParked) return;

    try {
      console.log("⏸️ Putting call on hold (parking in queue):", currentCall.parameters.CallSid);
      toast.info("Colocando em espera...");

      // Pega token fresco antes de chamar a edge function
      const { data: { session: currentSession }, error: sessionError } = await supabase.auth.getSession();

      if (sessionError || !currentSession) {
        console.error("❌ Failed to get session:", sessionError);
        throw new Error("Sessão expirada. Por favor, faça login novamente.");
      }

      console.log("🔑 Using fresh access token for queue-control");
      console.log("🔐 Token preview:", currentSession.access_token.substring(0, 20) + "...");
      console.log("📋 Request details:", {
        action: "park",
        callSid: currentCall.parameters.CallSid,
        callRecordId: currentCallRecordId,
      });

      // Chama edge function queue-control para estacionar
      // Passa explicitamente o Authorization header
      const { data, error } = await supabase.functions.invoke("queue-control", {
        body: {
          action: "park",
          callSid: currentCall.parameters.CallSid,
          callRecordId: currentCallRecordId,
        },
        headers: {
          Authorization: `Bearer ${currentSession.access_token}`,
        },
      });

      console.log("📥 Edge function response:", { data, error });

      if (error) throw error;
      if (!data?.success) throw new Error(data?.error || "Park failed");

      const queueName = data.queueName;
      console.log("✅ Call parked in queue:", queueName);

      // Buscar nome do contato do banco
      let contactName = "Cliente";
      if (currentCallRecordId) {
        // @ts-ignore - Supabase type instantiation too deep for nested joins
        const callQuery = supabase
          .from("calls")
          .select("contact_id, contacts(first_name, last_name)")
          .eq("id", currentCallRecordId)
          .single();
        const { data: callData } = await callQuery;
        
        if (callData?.contacts) {
          const contact = callData.contacts as any;
          contactName = `${contact.first_name || ""} ${contact.last_name || ""}`.trim() || "Cliente";
        }
      }

      // Salva informações para recuperar depois
      setParkedCallInfo({
        contactName,
        contactPhone: currentCallTargetNumber || "Unknown",
        direction: (currentCall as any).customParameters?.get("direction") || "outbound",
        callRecordId: currentCallRecordId || undefined,
        customerCallSid: data.customerCallSid, // CallSid do cliente na fila
      });

      setParkedQueue(queueName);
      setParkedClientName(contactName);
      setParkedClientPhone(currentCallTargetNumber || "Unknown");
      setIsParked(true);

      // ✅ Sincroniza ref IMEDIATAMENTE (não espera useEffect)
      isParkedRef.current = true;

      // Aguarda 1.5s para o cliente chegar na fila
      await new Promise((r) => setTimeout(r, 1500));

      // Desconecta SDR (backend only)
      currentCall.disconnect();

      toast.success("Cliente em espera (ouvindo música) 🎵");
    } catch (error) {
      console.error("Hold call error:", error);
      const errorMessage = error instanceof Error ? error.message : "Erro desconhecido";
      toast.error(`Falha ao colocar em espera: ${errorMessage}`);
    }
  }, [currentCall, isParked, currentCallRecordId, currentCallTargetNumber]);

  const resumeCall = useCallback(async () => {
    if (!parkedQueue || !user) return;

    try {
      console.log("▶️ Resuming call from queue:", parkedQueue);
      toast.info("Recuperando cliente...");

      // Marcar que está fazendo pickup para auto-aceitar quando a chamada chegar
      setIsPickingUp(true);

      // Verifica se usuário está autenticado
      const { data: { session: currentSession }, error: sessionError } = await supabase.auth.getSession();

      if (sessionError || !currentSession) {
        console.error("❌ Failed to get session:", sessionError);
        setIsPickingUp(false);
        throw new Error("Sessão expirada. Por favor, faça login novamente.");
      }

      console.log("🔑 User session valid for queue-control");

      // Chama edge function queue-control para pegar da fila
      // Passa explicitamente o Authorization header
      const { data, error } = await supabase.functions.invoke("queue-control", {
        body: {
          action: "pickup",
          queueName: parkedQueue,
          agentIdentity: user.id,
        },
        headers: {
          Authorization: `Bearer ${currentSession.access_token}`,
        },
      });

      if (error) {
        setIsPickingUp(false);
        throw error;
      }
      if (!data?.success) {
        setIsPickingUp(false);
        throw new Error(data?.error || "Pickup failed");
      }

      console.log("✅ Pickup initiated:", data.callSid);
      toast.success("Recuperando cliente da fila...");

      // Estado será limpo quando incoming call chegar e for auto-aceito
    } catch (error) {
      console.error("Resume call error:", error);
      const errorMessage = error instanceof Error ? error.message : "Erro desconhecido";
      toast.error(`Falha ao recuperar: ${errorMessage}`);
      setIsPickingUp(false);
    }
  }, [parkedQueue, user]);

  // Transferência fria - transfere cliente da fila diretamente para consultor
  const transferParkedToConsultant = useCallback(async (consultantUserId: string) => {
    if (!parkedQueue || !user) return;

    try {
      const currentCallSid = currentCall?.parameters?.CallSid;
      console.log("🔄 Cold transfer from queue to consultant:", { 
        parkedQueue, 
        consultantUserId,
        currentCallSid
      });
      toast.info("Transferindo cliente...");

      const { data: { session: currentSession }, error: sessionError } = await supabase.auth.getSession();

      if (sessionError || !currentSession) {
        console.error("❌ Failed to get session:", sessionError);
        throw new Error("Sessão expirada. Por favor, faça login novamente.");
      }

      // Chama edge function para transferir da fila para o consultor
      const { data, error } = await supabase.functions.invoke("queue-control", {
        body: {
          action: "transfer",
          queueName: parkedQueue,
          targetUserId: consultantUserId,
          callRecordId: parkedCallInfo?.callRecordId,
          consultantCallSid: currentCallSid, // Redirecionar chamada existente
          customerCallSid: parkedCallInfo?.customerCallSid, // Para validar que ainda está na fila
        },
        headers: {
          Authorization: `Bearer ${currentSession.access_token}`,
        },
      });

      // Tratar erro HTTP 409 (cliente desconectado) — supabase-js coloca o response no error.context
      if (error) {
        try {
          const ctxResp = (error as any)?.context;
          if (ctxResp && typeof ctxResp.json === "function") {
            const errBody = await ctxResp.json();
            if (errBody?.code === "CUSTOMER_DISCONNECTED") {
              toast.warning("Cliente desligou enquanto aguardava. Não foi possível transferir.");
              setIsParked(false);
              setParkedQueue(null);
              setParkedClientName(null);
              setParkedClientPhone(null);
              setParkedCallInfo(null);
              isParkedRef.current = false;
              return;
            }
            throw new Error(errBody?.error || error.message);
          }
        } catch (parseErr) {
          // Se não conseguir parsear, usa o erro original
          if (parseErr instanceof Error && parseErr.message !== error.message) throw parseErr;
        }
        throw error;
      }
      if (!data?.success) {
        if (data?.code === "CUSTOMER_DISCONNECTED") {
          toast.warning("Cliente desligou enquanto aguardava. Não foi possível transferir.");
          setIsParked(false);
          setParkedQueue(null);
          setParkedClientName(null);
          setParkedClientPhone(null);
          setParkedCallInfo(null);
          isParkedRef.current = false;
          return;
        }
        throw new Error(data?.error || "Transfer failed");
      }

      console.log("✅ Cold transfer iniciado:", data.callSid, "state:", data?.state);
      toast.success("Cliente transferido para o consultor com sucesso!");

      // Transferência confirmada pelo backend: limpa o banner "Cliente em espera" do SDR.
      // A perna do SDR será derrubada naturalmente pela Twilio quando a ponte SDR↔consultor
      // for desfeita pelo redirect — como `isParkedRef.current` agora é `false`, o handler
      // `disconnect` segue o fluxo normal e libera `currentCall`/`isOnCall` para a próxima chamada.
      setIsParked(false);
      setParkedQueue(null);
      setParkedClientName(null);
      setParkedClientPhone(null);
      setParkedCallInfo(null);
      isParkedRef.current = false;
    } catch (error) {
      console.error("Transfer parked call error:", error);
      const errorMessage = error instanceof Error ? error.message : "Erro desconhecido";
      toast.error(`Falha na transferência: ${errorMessage}. Cliente continua em espera — clique em Retomar para resgatar.`);
    }
  }, [parkedQueue, parkedCallInfo, user, currentCall]);

  return (
    <TwilioContext.Provider
      value={{
        device,
        isReady,
        isConnecting,
        isOnCall,
        isInitiatingCall,
        currentCall,
        incomingCall,
        error,
        callError,
        outgoingNumber,
        currentCallTargetNumber,
        currentCallRecordId,
        currentConsultantId,
        instantDisconnect,
        clearInstantDisconnect: () => setInstantDisconnect(false),
        isOnHold,
        isParked,
        parkedQueue,
        parkedClientName,
        parkedClientPhone,
        makeCall,
        makeConsultantCall,
        makeRingAllCall,
        joinDialerConference,
        isDialerCall,
        isDialerAudioReady,

        hangUp,
        toggleMute,
        sendDtmf,
        acceptIncomingCall,
        rejectIncomingCall,
        transferCall,
        transferParkedToConsultant,
        holdCall,
        resumeCall,
      }}
    >
      {children}
    </TwilioContext.Provider>
  );
}

let warnedOutsideTwilioProvider = false;

export function useTwilio() {
  const context = useContext(TwilioContext);
  if (context === undefined) {
    // Return safe default values instead of throwing error
    // This prevents crashes during initial render before provider is ready
    if (!warnedOutsideTwilioProvider) {
      warnedOutsideTwilioProvider = true;
      console.warn("useTwilio called outside TwilioProvider - returning defaults");
    }

    return {
      device: null,
      isReady: false,
      isConnecting: false,
      isOnCall: false,
      isInitiatingCall: false,
      currentCall: null,
      incomingCall: null,
      error: null,
      callError: null,
      outgoingNumber: null,
      currentCallTargetNumber: null,
      currentCallRecordId: null,
      currentConsultantId: null,
      isOnHold: false,
      isParked: false,
      parkedQueue: null,
      parkedClientName: null,
      parkedClientPhone: null,
      makeCall: async () => { throw new Error("Twilio not initialized"); },
      makeConsultantCall: async () => { throw new Error("Twilio not initialized"); },
      makeRingAllCall: async () => { throw new Error("Twilio not initialized"); },
      joinDialerConference: async () => null,
      isDialerCall: false,
      isDialerAudioReady: false,

      hangUp: () => {},
      toggleMute: () => false,
      sendDtmf: () => {},
      acceptIncomingCall: async () => {},
      rejectIncomingCall: () => {},
      transferCall: async () => {},
      transferParkedToConsultant: async () => {},
      holdCall: async () => {},
      resumeCall: async () => {},
      instantDisconnect: false,
      clearInstantDisconnect: () => {},
    };
  }
  return context;
}
